// The media strip (the brief, "Sections: media"): the trailer, then screenshots, as one swipe strip under the hero,
// with a full-screen viewer. Trailers are only ever files we host (rows.ts trailerSrc); with reduced motion the
// trailer is its poster. Screenshots come from the catalogue record's `shots` (art paths or banger.sh URLs; request
// to w12-catalogue / w12-art in zone-games.md); the hero's cover isn't repeated here. No media: no strip.

import type { Game } from "../../catalogue.ts";
import { h } from "../../dom.ts";
import { safeCover, trailerSrc } from "../../rows.ts";

export type MediaItem =
  | { type: "video"; src: string; poster: string | null; credit: string | null }
  | { type: "image"; src: string; alt: string };

export const MAX_SHOTS = 8;

/** The strip's items, in order. `still`: reduced motion (a trailer shows as its poster, or not at all). */
export function mediaItems(g: Pick<Game, "title" | "trailer"> & { shots?: unknown }, origin: string, still: boolean): MediaItem[] {
  const out: MediaItem[] = [];
  const t = g.trailer;
  if (t) {
    const src = trailerSrc(t.url, origin);
    const poster = safeCover(t.poster);
    const credit = typeof t.credit === "string" && t.credit ? t.credit : null;
    if (src && !still) out.push({ type: "video", src, poster, credit });
    else if (poster) out.push({ type: "image", src: poster, alt: `${g.title} trailer still` });
  }
  const shots = Array.isArray(g.shots) ? g.shots : [];
  const seen = new Set(out.map((m) => m.src));
  let n = 0;
  for (const s of shots) {
    const src = safeCover(s);
    if (!src || seen.has(src)) continue;
    seen.add(src);
    out.push({ type: "image", src, alt: `${g.title} screenshot ${++n}` });
    if (n >= MAX_SHOTS) break;
  }
  return out;
}

function reducedMotion(): boolean {
  return !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}

function videoTile(m: Extract<MediaItem, { type: "video" }>, title: string): HTMLElement {
  const video = h("video", { src: m.src, poster: m.poster, muted: true, autoplay: true, loop: true, playsinline: true, preload: "metadata", "aria-label": `${title} trailer (muted)` }) as HTMLVideoElement;
  video.muted = true; // the attribute alone doesn't mute a script-made video in every browser
  const toggle = h("button", { type: "button", class: "gt-media-pause", "aria-label": "Pause the trailer" }, "Pause");
  toggle.addEventListener("click", () => {
    if (video.paused) void video.play().catch(() => {});
    else video.pause();
  });
  const sync = () => {
    toggle.textContent = video.paused ? "Play" : "Pause";
    toggle.setAttribute("aria-label", video.paused ? "Play the trailer" : "Pause the trailer");
  };
  video.addEventListener("play", sync);
  video.addEventListener("pause", sync);
  video.addEventListener("error", () => video.replaceWith(m.poster ? h("img", { src: m.poster, alt: `${title} trailer still` }) : h("span", { class: "gt-media-gone" })), { once: true });
  return h("li", { class: "gt-media-tile is-video" }, video, toggle, m.credit ? h("span", { class: "gt-media-credit" }, `Trailer by ${m.credit}`) : null);
}

/** The full-screen viewer for screenshots: arrows, swipe-free (the strip swipes), Esc closes. */
function viewer(images: Extract<MediaItem, { type: "image" }>[]): { open: (i: number) => void; el: HTMLDialogElement } {
  let at = 0;
  const img = h("img", { class: "gt-view-img", alt: "" }); // in the dialog only once it opens (no empty <img> on the page)
  const count = h("span", { class: "gt-view-count", "aria-live": "polite" });
  const show = (i: number) => {
    at = (i + images.length) % images.length;
    img.setAttribute("src", images[at]!.src);
    img.setAttribute("alt", images[at]!.alt);
    count.textContent = `${at + 1} / ${images.length}`;
  };
  const many = images.length > 1;
  const dlg = h(
    "dialog",
    { class: "gt-view", "aria-label": "Screenshots" },
    h("button", { type: "button", class: "gt-view-close", "aria-label": "Close", onclick: () => dlg.close() }, "×"),
    many ? h("button", { type: "button", class: "gt-view-prev", "aria-label": "Previous screenshot", onclick: () => show(at - 1) }, "‹") : null,
    many ? h("button", { type: "button", class: "gt-view-next", "aria-label": "Next screenshot", onclick: () => show(at + 1) }, "›") : null,
    many ? count : null,
  ) as HTMLDialogElement;
  dlg.addEventListener("keydown", (e: KeyboardEvent) => {
    if (e.key === "ArrowLeft" && many) show(at - 1);
    if (e.key === "ArrowRight" && many) show(at + 1);
  });
  dlg.addEventListener("click", (e) => e.target === dlg && dlg.close()); // the backdrop
  return {
    el: dlg,
    open: (i) => {
      show(i);
      if (!img.isConnected) dlg.insertBefore(img, dlg.querySelector(".gt-view-next") ?? dlg.querySelector(".gt-view-count"));
      if (typeof dlg.showModal === "function") dlg.showModal();
      else window.open(images[at]!.src, "_blank", "noopener");
    },
  };
}

/** The strip, or null when there's nothing to show. */
export function mediaStrip(g: Game): HTMLElement | null {
  const items = mediaItems(g as Game & { shots?: unknown }, location.origin, reducedMotion());
  if (!items.length) return null;
  const images = items.filter((m): m is Extract<MediaItem, { type: "image" }> => m.type === "image");
  const view = images.length ? viewer(images) : null;
  const tiles = items.map((m) => {
    if (m.type === "video") return videoTile(m, g.title);
    const i = images.indexOf(m);
    const pic = h("img", { src: m.src, alt: m.alt, loading: i > 1 ? "lazy" : null, decoding: "async" });
    pic.addEventListener(
      "error",
      () => {
        const li = pic.closest("li");
        const ul = li?.parentElement;
        li?.remove();
        if (ul && !ul.children.length) ul.closest("section")?.setAttribute("hidden", ""); // nothing left: no strip
      },
      { once: true },
    );
    return h("li", { class: "gt-media-tile" }, h("button", { type: "button", class: "gt-media-open", "aria-label": `View ${m.alt} larger`, onclick: () => view?.open(i) }, pic));
  });
  return h(
    "section",
    { class: `gt-media${items.length === 1 ? " is-single" : ""}`, "aria-label": "Trailer and screenshots" },
    h("h2", { class: "gt-sr" }, "Trailer and screenshots"),
    h("ul", { class: "gt-media-strip", tabindex: 0 }, tiles),
    view?.el ?? null,
  );
}
